/**
 * Profile metadata queries by viewer scope.
 */
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseQueryOptions,
    type UseQueryResult,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { createApiClient } from 'lib/api/createApiClient';
import { parseError } from 'lib/api/errors/parseError';
import { useAuthStore } from 'lib/stores/authStore';
import type { ApiErrorType } from 'models';
import type { SearchProfilesResultVo } from 'models/search';
import config from 'utils/config';
import type { LeaderboardTimePeriod } from 'utils/time';

import type { SocialInnerCircleState } from './socialInnerCircle';
import { resolveSocialViewerScope, type SocialViewerScope } from './socialViewerScope';
import { removeEmpty } from './utils';

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

const STALE_TIME = 60 * 1000;
const PROFILE_METRICS_REFETCH_INTERVAL_MS = 60 * 1000;

/**
 * Metadata shape; withheld fields are omitted by the backend.
 */
export interface SocialProfileMetadataVo {
    social_id: string;
    nickname: string;
    profile_image_path: string;
    pending_profile_image_path: string;
    description: string;
    joined_at?: string;
    blocked?: boolean;
    follower_count?: number;
    following_count?: number;
    posts_count?: number;
    top_categories?: string[];
    /** Per-surface visibility; absent means fail-open. */
    visibility_state?: Record<string, string>;
}

export interface GetSocialProfileMetadataResponse {
    /** Absent when the user has not created a social profile yet. */
    social_profile?: SocialProfileMetadataVo;
    inner_circle?: SocialInnerCircleState;
}

/**
 * Header metrics. `*_fp` strings stay precise until display.
 */
export interface SocialProfileMetricsData {
    volume?: number;
    volume_fp?: string;
    /** Integer count of 1/10000-dollar units. */
    dollars_traded?: number;
    pnl?: number;
    open_interest?: number;
    open_interest_fp?: string;
    num_markets_traded?: number;
}

export interface GetSocialProfileMetricsResponse {
    metrics: SocialProfileMetricsData;
    social_id?: string;
}

interface UpdateGeneratedProfileImageResponse {
    profile_image_path: string;
}

type ProfileMetadataQueryResult = UseQueryResult<GetSocialProfileMetadataResponse, Error>;
type ProfileQueryOptions<T> = Pick<UseQueryOptions<T, Error>, 'enabled' | 'refetchInterval'>;
type ProfileMetadataQueryOptions = ProfileQueryOptions<GetSocialProfileMetadataResponse>;
type ProfileMetricsQueryResult = UseQueryResult<GetSocialProfileMetricsResponse, Error>;
type ProfileMetricsQueryOptions = ProfileQueryOptions<GetSocialProfileMetricsResponse>;

/**
 * `viewerUserId` scopes profile_view data across account switches.
 */
export const socialProfileKeys = {
    all: ['socialProfile'] as const,
    byScope: (scope: SocialViewerScope, identifier: string | null | undefined, viewerUserId?: string | null) =>
        [...socialProfileKeys.all, scope, removeEmpty({ identifier, viewerUserId })] as const,
};

/**
 * Optional server filters used by the leaderboard "you" row.
 */
export interface SocialProfileMetricsFilters {
    timePeriod?: LeaderboardTimePeriod;
    category?: string;
}

export const socialProfileMetricsKeys = {
    all: ['socialProfileMetrics'] as const,
    byScope: (
        scope: SocialViewerScope,
        identifier: string | null | undefined,
        viewerUserId?: string | null,
        filters?: SocialProfileMetricsFilters,
    ) =>
        [
            ...socialProfileMetricsKeys.all,
            scope,
            removeEmpty({ identifier, viewerUserId, timePeriod: filters?.timePeriod, category: filters?.category }),
        ] as const,
};

export function ownSocialProfileQueryOptions(userId: string | null | undefined) {
    return {
        queryKey: socialProfileKeys.byScope('own', userId),
        queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<GetSocialProfileMetadataResponse> => {
            const res = await api.get<GetSocialProfileMetadataResponse>(`users/${userId}/social/profile`, { signal });
            return res.data;
        },
        enabled: !!userId,
        staleTime: STALE_TIME,
    };
}

/** Own profile — keyed by the logged-in user id, hits the authed endpoint. */
export function useSocialProfile(options?: ProfileMetadataQueryOptions): ProfileMetadataQueryResult {
    const userId = useAuthStore((s) => s.userId);
    return useQuery({
        ...ownSocialProfileQueryOptions(userId),
        enabled: !!userId && (options?.enabled ?? true),
    });
}

/** Signed-out public profile by nickname. */
export function usePublicSocialProfile(
    nickname: string | undefined,
    options?: ProfileMetadataQueryOptions,
): ProfileMetadataQueryResult {
    return useQuery({
        queryKey: socialProfileKeys.byScope('public', nickname),
        queryFn: async ({ signal }) => {
            const res = await api.get<GetSocialProfileMetadataResponse>(
                `social/profile${buildNicknameQuery(nickname)}`,
                { signal },
            );
            return res.data;
        },
        enabled: !!nickname && (options?.enabled ?? true),
        staleTime: STALE_TIME,
    });
}

/**
 * Poll pending Inner Circle status; acceptance happens on another device.
 */
const PENDING_VIEWER_REFETCH_INTERVAL = 30 * 1000;

export function resolveProfileViewRefetchInterval(data: GetSocialProfileMetadataResponse | undefined): number | false {
    return data?.inner_circle?.viewer_status === 'pending' ? PENDING_VIEWER_REFETCH_INTERVAL : false;
}

/**
 * Signed-in viewer route; focus refetch catches Inner Circle changes.
 */
export function useSocialProfileView(
    nickname: string | undefined,
    options?: ProfileMetadataQueryOptions,
): ProfileMetadataQueryResult {
    const viewerUserId = useAuthStore((s) => s.userId);
    return useQuery({
        queryKey: socialProfileKeys.byScope('profile_view', nickname, viewerUserId),
        queryFn: async ({ signal }) => {
            const res = await api.get<GetSocialProfileMetadataResponse>(
                `users/${viewerUserId}/social/profile_view${buildNicknameQuery(nickname)}`,
                { signal },
            );
            return res.data;
        },
        enabled: !!nickname && !!viewerUserId && (options?.enabled ?? true),
        staleTime: STALE_TIME,
        refetchOnWindowFocus: true,
        refetchInterval: (query) => resolveProfileViewRefetchInterval(query.state.data),
    });
}

function buildNicknameQuery(nickname: string | undefined): string {
    return nickname ? `?nickname=${encodeURIComponent(nickname)}` : '';
}

export interface UseProfileMetadataResult {
    scope: SocialViewerScope;
    query: ProfileMetadataQueryResult;
}

/**
 * Runs all profile hooks but enables only the viewer-scoped one.
 */
export function useProfileMetadata({
    nickname,
    ownProfile,
}: {
    nickname?: string;
    ownProfile: boolean;
}): UseProfileMetadataResult {
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
    const scope = resolveSocialViewerScope({ ownProfile, isLoggedIn });

    const own = useSocialProfile({ enabled: scope === 'own' });
    const view = useSocialProfileView(nickname, { enabled: scope === 'profile_view' });
    const pub = usePublicSocialProfile(nickname, { enabled: scope === 'public' });

    const byScope: Record<SocialViewerScope, ProfileMetadataQueryResult> = {
        own,
        profile_view: view,
        public: pub,
    };

    return { scope, query: byScope[scope] };
}

/**
 * Own trade metrics, optionally filtered for the leaderboard "you" row.
 */
export function useSocialProfileMetricsOwn(
    options?: ProfileMetricsQueryOptions & SocialProfileMetricsFilters,
): ProfileMetricsQueryResult {
    const userId = useAuthStore((s) => s.userId);
    const filters: SocialProfileMetricsFilters = { timePeriod: options?.timePeriod, category: options?.category };
    return useQuery({
        queryKey: socialProfileMetricsKeys.byScope('own', userId, undefined, filters),
        queryFn: async ({ signal }) => {
            const res = await api.get<GetSocialProfileMetricsResponse>(`users/${userId}/social/profile/metrics`, {
                signal,
                params: removeEmpty({ time_period: filters.timePeriod, category: filters.category }),
            });
            return res.data;
        },
        enabled: !!userId && (options?.enabled ?? true),
        refetchInterval: options?.refetchInterval,
        staleTime: STALE_TIME,
    });
}

/**
 * Other-profile metrics, scoped for Inner Circle visibility.
 */
export function useSocialProfileMetricsByNickname(
    scope: Extract<SocialViewerScope, 'public' | 'profile_view'>,
    nickname: string | undefined,
    options?: ProfileMetricsQueryOptions,
): ProfileMetricsQueryResult {
    const viewerUserId = useAuthStore((s) => s.userId);
    const isViewer = scope === 'profile_view';
    const path = isViewer
        ? `users/${viewerUserId}/social/profile_view/metrics${buildNicknameQuery(nickname)}`
        : `social/profile/metrics${buildNicknameQuery(nickname)}`;
    return useQuery({
        queryKey: socialProfileMetricsKeys.byScope(scope, nickname, isViewer ? viewerUserId : undefined),
        queryFn: async ({ signal }) => {
            const res = await api.get<GetSocialProfileMetricsResponse>(path, { signal });
            return res.data;
        },
        enabled: !!nickname && (!isViewer || !!viewerUserId) && (options?.enabled ?? true),
        refetchInterval: options?.refetchInterval,
        staleTime: STALE_TIME,
    });
}

export interface UseProfileMetricsResult {
    scope: SocialViewerScope;
    query: ProfileMetricsQueryResult;
}

/** Scope-picking wrapper mirroring `useProfileMetadata`, for the header metrics. */
export function useProfileMetrics({
    nickname,
    ownProfile,
}: {
    nickname?: string;
    ownProfile: boolean;
}): UseProfileMetricsResult {
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
    const scope = resolveSocialViewerScope({ ownProfile, isLoggedIn });

    const own = useSocialProfileMetricsOwn({
        enabled: scope === 'own',
        refetchInterval: PROFILE_METRICS_REFETCH_INTERVAL_MS,
    });
    const view = useSocialProfileMetricsByNickname('profile_view', nickname, {
        enabled: scope === 'profile_view',
        refetchInterval: PROFILE_METRICS_REFETCH_INTERVAL_MS,
    });
    const pub = useSocialProfileMetricsByNickname('public', nickname, {
        enabled: scope === 'public',
        refetchInterval: PROFILE_METRICS_REFETCH_INTERVAL_MS,
    });

    const byScope: Record<SocialViewerScope, ProfileMetricsQueryResult> = {
        own,
        profile_view: view,
        public: pub,
    };

    return { scope, query: byScope[scope] };
}

function patchOwnSocialProfileCache(
    queryClient: ReturnType<typeof useQueryClient>,
    userId: string | null,
    patch: Partial<SocialProfileMetadataVo>,
): void {
    if (!userId) {
        return;
    }

    queryClient.setQueryData<GetSocialProfileMetadataResponse>(socialProfileKeys.byScope('own', userId), (current) =>
        current?.social_profile
            ? {
                  ...current,
                  social_profile: {
                      ...current.social_profile,
                      ...patch,
                  },
              }
            : current,
    );
}

export function useUpdateSocialProfileNickname() {
    const queryClient = useQueryClient();
    const userId = useAuthStore((s) => s.userId);

    return useMutation({
        mutationFn: async (nickname: string): Promise<void> => {
            if (!userId) {
                throw new Error('Cannot update social profile nickname when logged out');
            }

            await api.put(`users/${userId}/social/update_nickname`, { nickname });
        },
        onSuccess: (_data, nickname) => {
            patchOwnSocialProfileCache(queryClient, userId, { nickname });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: socialProfileKeys.all });
        },
    });
}

export function useUpdateSocialProfileDescription() {
    const queryClient = useQueryClient();
    const userId = useAuthStore((s) => s.userId);

    return useMutation({
        mutationFn: async (description: string): Promise<void> => {
            if (!userId) {
                throw new Error('Cannot update social profile description when logged out');
            }

            await api.put(`users/${userId}/social/description`, { description });
        },
        onSuccess: (_data, description) => {
            patchOwnSocialProfileCache(queryClient, userId, { description });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: socialProfileKeys.all });
        },
    });
}

export function useUpdateSocialPendingProfileImage() {
    const queryClient = useQueryClient();
    const userId = useAuthStore((s) => s.userId);

    return useMutation({
        mutationFn: async (profileImage: string): Promise<void> => {
            if (!userId) {
                throw new Error('Cannot update social profile image when logged out');
            }

            await api.put(`users/${userId}/social/update_pending_profile_image`, { profile_image: profileImage });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: socialProfileKeys.all });
        },
    });
}

export function useUpdateGeneratedProfileImage() {
    const queryClient = useQueryClient();
    const userId = useAuthStore((s) => s.userId);

    return useMutation({
        mutationFn: async (shuffleIndex: number): Promise<string> => {
            if (!userId) {
                throw new Error('Cannot update generated social profile image when logged out');
            }

            const response = await api.put<UpdateGeneratedProfileImageResponse>(
                `users/${userId}/social/update_generated_profile_image`,
                { shuffle_index: shuffleIndex },
            );
            return response.data.profile_image_path;
        },
        onSuccess: (profileImagePath) => {
            patchOwnSocialProfileCache(queryClient, userId, { profile_image_path: profileImagePath });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: socialProfileKeys.all });
        },
    });
}

/* -------------------------------------------------------------------------- */
/*                       PLAIN API FUNCTIONS (non-hook)                        */
/* For use outside React — e.g. zustand stores that can't call hooks.        */
/* Errors are rethrown as ServerError so callers get structured error fields. */
/* -------------------------------------------------------------------------- */

export async function updateProfileNickname(userId: string, nickname: string): Promise<void> {
    try {
        await api.put(`users/${userId}/social/update_nickname`, { nickname });
    } catch (err) {
        throw parseError(err as AxiosError<ApiErrorType>);
    }
}

export async function updatePendingProfileImage(userId: string, image: string): Promise<void> {
    try {
        await api.put(`users/${userId}/social/update_pending_profile_image`, { profile_image: image });
    } catch (err) {
        throw parseError(err as AxiosError<ApiErrorType>);
    }
}

export async function updateGeneratedProfileImage(
    userId: string,
    shuffleIndex: number,
): Promise<{ profile_image_path: string }> {
    try {
        const response = await api.put<{ profile_image_path: string }>(
            `users/${userId}/social/update_generated_profile_image`,
            { shuffle_index: shuffleIndex },
        );
        return response.data;
    } catch (err) {
        throw parseError(err as AxiosError<ApiErrorType>);
    }
}

export async function searchSocialProfiles(
    params: { query?: string; cursor?: string; limit?: number },
    signal?: AbortSignal,
): Promise<SearchProfilesResultVo> {
    const res = await api.get<SearchProfilesResultVo>('search/social_profiles', {
        params: removeEmpty(params),
        signal,
    });
    return res.data;
}
